import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Users,
  Award,
  BookOpen,
  Star,
  StarHalf,
  Book,
  Home,
  X,
} from 'lucide-react';
import { asset } from '../../../shared/utils/asset';
import { SEO } from '../../../shared/components';
import { useSettingsContext } from '../../../shared/context/SettingsContext';
import { useEvents } from '../../../features/events/hooks/useEvents';
import { Calendar, MapPin, Image } from 'lucide-react';


// NOTE: Replace this with your actual asset resolver logic or hardcoded URLs
// For demonstration, we'll use a placeholder function.

// --- Interface Definitions ---
interface TestimonialCardProps {
  rating: number;
  text: string;
  name: string;
  role: string;
  initials: string;
  className?: string; 
}

interface SuccessStoryProps {
  name: string;
  exam: string;
  percentage: string;
  testimonial: string;
  initial: string;
  className?: string; 
}

interface FeatureCardProps {
  icon: React.ReactNode; 
  title: string;
  description: string;
  className?: string;
}

interface ProgramCardProps {
  tag: string;
  title: string;
  description: string;
  link: string;
  className?: string;
}

// --- FeatureCard Component ---

const FeatureCard: React.FC<FeatureCardProps> = ({ 
    icon, 
    title, 
    description, 
    className = "" 
}) => {

    const renderIconWithStyles = () => {
        
        if (React.isValidElement(icon)) {
            // FIX: Explicitly cast the icon to a ReactElement that accepts an optional 'className' prop.
            const iconElement = icon as React.ReactElement<{ className?: string }>;
            
            const existingClassName = iconElement.props.className || '';
            
            // ADJUSTMENT: Use 'text-accent' for theme consistency, matching the light blue visual style.
            return React.cloneElement(iconElement, { 
                className: `w-6 h-6 text-accent ${existingClassName}` 
            });
        }
        
        return icon;
    };
    
    return (
        <div className={`p-6 bg-white rounded-xl shadow-lg ${className}`}
         onLoad={e => {
          // This ensures the animation is applied only after the content/images are loaded (or immediately if already loaded)
          const target = e.currentTarget as HTMLDivElement;
          target.style.opacity = '1'; // Reset opacity
          target.classList.add('animate-full-page-slide'); // Apply the animation
      }}>
            
            {/* ICON WRAPPER: Use bg-accent/10 for the faint light blue background seen in the screenshot */}
            <div className="w-12 h-12 flex items-center justify-center rounded-full mb-4 bg-accent/10"> 
                {renderIconWithStyles()}
            </div>
            
            <h3 className="text-xl font-semibold mb-2 text-foreground">{title}</h3>
            <p className="text-muted-foreground">{description}</p>
        </div>
    );
};

// --- Other Functional Components ---

const ProgramCard: React.FC<ProgramCardProps> = ({ tag, title, description, link, className = "" }) => (
  <div className={`bg-white p-8 rounded-xl shadow-lg transform transition-all duration-300 hover:-translate-y-1 animate-slide-up ${className}`}>
    <span className="text-sm font-medium text-muted-foreground mb-4 block">{tag}</span>
    <h3 className="text-2xl font-semibold text-foreground mb-3">{title}</h3>
    <p className="text-muted-foreground mb-6">{description}</p>
    <Link to={link} className="text-accent font-semibold inline-flex items-center group">
      Learn more
      <ArrowRight className="w-4 h-4 ml-2 transition-transform duration-300 group-hover:translate-x-1" />
    </Link>
  </div>
);

const SuccessStory: React.FC<SuccessStoryProps> = ({ name, exam, percentage, testimonial, initial, className = "" }) => (
  <div className={`bg-white p-8 rounded-xl shadow-lg animate-slide-up ${className}`}>
    <div className="flex items-start gap-4 mb-6">
      <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0">
        <span className="text-lg font-bold text-accent">{initial}</span>
      </div>
      <div>
        <h3 className="text-lg font-semibold text-foreground">{name}</h3>
        <p className="text-sm text-muted-foreground">{exam}</p>
      </div>
    </div>
    <div className="mb-4">
      <span className="text-5xl font-display font-bold text-foreground">{percentage}</span>
    </div>
    <p className="text-base text-muted-foreground">"{testimonial}"</p>
  </div>
);

const TestimonialCard: React.FC<TestimonialCardProps> = ({ rating, text, name, role, initials, className = "" }) => (
  <div className={`bg-white p-8 rounded-xl shadow-lg animate-slide-up ${className}`}>
    <div className="flex items-center mb-4">
      {[...Array(Math.floor(rating))].map((_, i) => (
        <Star key={i} className="w-4 h-4 text-accent fill-accent" />
      ))}
      {rating % 1 !== 0 && <StarHalf className="w-4 h-4 text-accent" />}
    </div>
    <p className="text-base text-foreground mb-6 leading-relaxed">"{text}"</p>
    <div className="flex items-center">
      <div className="w-10 h-10 rounded-full bg-cyan-100 flex items-center justify-center mr-3 flex-shrink-0">
        <span className="font-medium text-foreground">{initials}</span>
      </div>
      <div>
        <h4 className="font-semibold text-foreground">{name}</h4>
        <p className="text-sm text-muted-foreground">{role}</p>
      </div>
    </div>
  </div>
);


// --- Events Section Component ---
const EventsSection: React.FC = () => {
  const { events, loading, error } = useEvents(false);
  const recentEvents = events.slice(0, 4);

  if (loading) {
    return (
      <section className="py-24 bg-accent">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="text-center max-w-4xl mx-auto mb-16">
            <h2 className="text-4xl font-display font-semibold text-white mb-4">Our Events</h2>
            <p className="text-lg text-white/80">Explore memorable moments from our activities and celebrations.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="animate-pulse">
                <div className="bg-white/20 aspect-video rounded-t-xl"></div>
                <div className="bg-white p-4 rounded-b-xl">
                  <div className="bg-gray-200 h-5 rounded mb-2"></div>
                  <div className="bg-gray-200 h-4 rounded w-2/3"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (error || recentEvents.length === 0) {
    return (
      <section className="py-24 bg-accent">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="text-center max-w-4xl mx-auto mb-16">
            <h2 className="text-4xl font-display font-semibold text-white mb-4">Our Events</h2>
            <p className="text-lg text-white/80">Explore memorable moments from our activities and celebrations.</p>
          </div>
          <div className="text-center py-12">
            <div className="bg-white rounded-xl p-12 max-w-md mx-auto shadow-lg">
              <Calendar className="mx-auto h-12 w-12 text-gray-300 mb-4" />
              <h3 className="text-lg font-semibold text-gray-700 mb-2">Events Coming Soon</h3>
              <p className="text-gray-500">We're preparing amazing event galleries for you!</p>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-24 bg-accent">
      <div className="container mx-auto px-4 max-w-7xl">
        <div className="text-center max-w-4xl mx-auto mb-16 animate-slide-up">
          <h2 className="text-4xl font-display font-semibold text-white mb-4">Our Events</h2>
          <p className="text-lg text-white/80 max-w-3xl mx-auto">
            Explore memorable moments from our activities and celebrations.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {recentEvents.map((event) => {
            const itemCount = event._count?.items || 0;
            const formattedDate = event.eventDate
              ? new Date(event.eventDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
              : null;

            return (
              <Link
                key={event.id}
                to={`/events/${event.id}`}
                className={`bg-white rounded-xl shadow-lg overflow-hidden group hover:shadow-xl transition-all duration-300 hover:-translate-y-1 animate-slide-up`}
              >
                {/* Cover Image */}
                <div className="relative aspect-video overflow-hidden bg-gradient-to-br from-cyan-500 to-blue-600">
                  {event.coverImageUrl ? (
                    <img
                      src={event.coverImageUrl}
                      alt={event.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      loading="lazy"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Image className="w-12 h-12 text-white/50" />
                    </div>
                  )}

                  {/* Item count badge */}
                  <div className="absolute top-2 right-2 bg-black/60 backdrop-blur-sm text-white px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1">
                    <Image className="w-3 h-3" />
                    <span>{itemCount}</span>
                  </div>
                </div>

                {/* Content */}
                <div className="p-4">
                  <h3 className="text-lg font-bold text-gray-900 mb-1 line-clamp-1 group-hover:text-cyan-600 transition-colors">
                    {event.title}
                  </h3>

                  <div className="flex flex-wrap gap-2 text-xs text-gray-500">
                    {formattedDate && (
                      <div className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        <span>{formattedDate}</span>
                      </div>
                    )}
                    {event.location && (
                      <div className="flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        <span className="line-clamp-1">{event.location}</span>
                      </div>
                    )}
                  </div>
                </div>
              </Link>
            );
          })}
        </div>

        <div className="text-center">
          <Link
            to="/events"
            className="bg-white hover:bg-gray-100 text-cyan-600 font-semibold py-3 px-8 rounded-lg shadow-md transition duration-300 inline-flex items-center group"
          >
            View All Events
            <ArrowRight className="w-4 h-4 ml-2 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </div>
      </div>
    </section>
  );
};

// --- Main Page Component ---
const TuitionPointHome: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { settings } = useSettingsContext();

  useEffect(() => {
    return () => {
      document.body.classList.remove('overflow-hidden');
    };
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
    document.body.classList.toggle('overflow-hidden', !isMenuOpen);
  };
  
  // Data definitions 
  const features = [
      { icon: <Users />, title: 'Personalized Approach', description: 'We create individualized learning plans tailored to each student’s unique needs, learning style, and goals.' },
      { icon: <Award />, title: 'Expert Teachers', description: 'Our team consists of qualified educators with extensive experience in their subject areas and passion for teaching.' },
      { icon: <BookOpen />, title: 'Multiple Curricula', description: 'We support various curricula including CBSE, IGCSE, IB, Federal, and Bangladesh Board to meet diverse educational needs.' },
    ];

  const programs = [
      { tag: 'Grades K.G-12', title: 'Core Subjects', description: 'Mathematics, Physics, Chemistry, Biology, English, and more tailored to your curriculum.', link: '/services' },
      { tag: 'All Levels', title: 'Specialized Programs', description: 'Crash courses, entrance test preparation, and foundation courses for advanced learning.', link: '/services' },
      { tag: 'Summer', title: 'Summer Courses', description: 'Keep the learning momentum going during summer with our engaging and enriching programs.', link: '/services' },
    ];

  const successStories = [
    { name: "Hiba Nisam", exam: "CBSE Board Exam", percentage: "98%", testimonial: "Tuition Point helped me understand complex concepts and gave me the confidence to excel in my board exams.", initial: "H" },
    { name: "Niranjan", exam: "CBSE Board Exam", percentage: "96.2%", testimonial: "The personalized attention from teachers at Tuition Point made all the difference in my academic performance.", initial: "N" }
  ];

  const testimonials = [
    { rating: 5, text: "I've seen remarkable improvement in my daughter's academic skills over just 3 months at Tuition Point. The teachers are supportive and motivating.", name: "Ponnu Menu", role: "Parent", initials: "PM" },
    { rating: 5, text: "The worksheets and teaching methods at Tuition Point have really helped my child grasp difficult concepts. I'm very happy with their approach to education.", name: "Aatha Sharma", role: "Parent", initials: "AS" },
    { rating: 4.5, text: "Thanks to the coaching at Tuition Point, my kids gained admission to BPS. The teachers' dedication made all the difference in their preparation.", name: "Kesavan Nagaraj", role: "Parent", initials: "KN" }
  ];

  const quickLinks = [
    { icon: <Book className="w-6 h-6 text-accent" />, title: "Curriculum", description: "Explore the various curricula we support and our approach to each educational system.", link: "/curriculum" },
    { icon: <Users className="w-6 h-6 text-accent" />, title: "Parent & Student Support", description: "Learn about our comprehensive support systems for both students and parents.", link: "/parent-support" },
    { icon: <Home className="w-6 h-6 text-accent" />, title: "Our Campus", description: "Take a tour of our modern facilities designed to provide an optimal learning environment.", link: "/campus" }
  ];


  return (
    <div className="bg-accent text-white min-h-screen">
      <SEO
        title="Tuition Point Qatar - Expert Tutoring for K-12 | CBSE, IGCSE, IB in Al Wakra"
        description="Tuition Point Education Center in Al Wakra, Qatar offers personalized tutoring for grades K-12. Expert teachers for CBSE, IGCSE, IB, Federal & Bangladesh Board. Licensed by Qatar Ministry of Education."
        keywords="tuition center Qatar, tutoring Al Wakra, CBSE tuition Qatar, IGCSE tutoring Qatar, IB coaching Qatar, K-12 education Qatar, Bangladesh Board Qatar, Federal Board tutoring, private tutoring Al Wakra, personalized education Qatar"
      />

      {/* --------------------- Mobile Menu Overlay --------------------- */}
      <div 
        id="mobile-menu" 
        className={`fixed inset-0 z-50 bg-white transition-transform duration-300 ${isMenuOpen ? 'translate-x-0' : 'translate-x-full'}`}
      >
        <div className="container mx-auto px-4 h-full flex flex-col py-8 max-w-7xl">
          <div className="flex justify-between items-center mb-12">
            <Link to="/" className="flex items-center space-x-2">
              <div className="w-10 h-10 rounded-full bg-accent flex items-center justify-center text-white">
                <span className="font-display font-bold text-lg">TP</span>
              </div>
              <span className="font-display font-bold text-primary text-lg">Tuition Point</span>
            </Link>
            <button onClick={toggleMenu} aria-label="Close Menu">
              <X className="w-6 h-6 text-primary" />
            </button>
          </div>
          <nav className="flex flex-col space-y-6 text-xl">
            <Link to="/" onClick={toggleMenu} className="nav-link text-foreground hover:text-accent transition-colors">Home</Link>
            <Link to="/about" onClick={toggleMenu} className="nav-link text-foreground hover:text-accent transition-colors">About Us</Link>
            <Link to="/services" onClick={toggleMenu} className="nav-link text-foreground hover:text-accent transition-colors">Services</Link>
            <Link to="/team" onClick={toggleMenu} className="nav-link text-foreground hover:text-accent transition-colors">Our Team</Link>
            <Link to="/achievements" onClick={toggleMenu} className="nav-link text-foreground hover:text-accent transition-colors">Success Stories</Link>
            <Link to="/contact" onClick={toggleMenu} className="nav-link text-foreground hover:text-accent transition-colors">Contact</Link>
            <Link to="/enrollment" onClick={toggleMenu} className="btn-primary w-full text-center mt-4">Enroll Now</Link>
          </nav>
        </div>
      </div>
      
      {/* --------------------- Announcement Bar --------------------- */}
      {(() => {
        // Collect all active notifications
        const notifications = [
          settings?.notification1,
          settings?.notification2,
          settings?.notification3
        ].filter(Boolean);

        if (notifications.length === 0) return null;

        return (
          <div className="bg-yellow-400 py-3 overflow-hidden">
            <div className="animate-scroll whitespace-nowrap hover:pause-animation">
              {/* Repeat notifications 3 times for smooth infinite scroll */}
              {[...Array(3)].map((_, repeatIndex) => (
                <React.Fragment key={repeatIndex}>
                  {notifications.map((notification, index) => (
                    <React.Fragment key={`${repeatIndex}-${index}`}>
                      <span className="inline-block px-8 text-gray-900 font-semibold">
                        {notification}
                      </span>
                      {/* Show dot after each notification except the last one in the last repeat */}
                      {!(repeatIndex === 2 && index === notifications.length - 1) && (
                        <span className="inline-block px-2 text-gray-900">•</span>
                      )}
                    </React.Fragment>
                  ))}
                </React.Fragment>
              ))}
            </div>
          </div>
        );
      })()}

      {/* --------------------- Hero Section --------------------- */}
      <section className="pt-16 pb-20 md:pt-20 md:pb-24 overflow-hidden bg-accent">
        <div className="container mx-auto px-4 max-w-7xl">
          {/* ADJUSTMENT: Ensured grid items are centered vertically */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="header-content">
              <div className="animate-fade-in">
                <span className="text-sm font-medium text-white/70 mb-2 block">Licensed by the Ministry of Education & Higher Education</span>
              </div>
              <h1 className="text-white text-4xl md:text-5xl lg:text-6xl font-extrabold leading-tight mb-6 animate-slide-up">
                Expert K-12 Tutoring in <br/>Al Wakra, Qatar
              </h1>
              <p className="text-lg text-white mb-8 max-w-lg animate-slide-up">
                Welcome to <span className="text-yellow-300 font-semibold">Tuition Point Education Center</span>. We provide personalized tutoring for CBSE, IGCSE, IB, Federal & Bangladesh Board students from kindergarten to grade 12 with expert teachers and proven results.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 animate-slide-up">

                <div className="flex items-center gap-4">

                    <Link
                        to="/enrollment"
                        className="bg-white hover:bg-gray-100 text-cyan-600 font-semibold py-3 px-6 rounded-lg shadow-md transition duration-200 flex items-center justify-center whitespace-nowrap"
                    >
                        Enroll Today

                        <ArrowRight className="w-4 h-4 ml-2" />
                    </Link>


                    <Link
                        to="/contact"
                        className="bg-white/10 hover:bg-white/20 border border-white/30 text-white font-semibold py-3 px-6 rounded-lg shadow-md transition duration-200 whitespace-nowrap"
                    >
                        Contact Us
                    </Link>
                </div>
              </div>
            </div>
            <div className="relative">
              <img
                src={settings?.heroImageUrl || asset('Home/home.jpg')}
                alt="Students studying at Tuition Point"
                className="hero-image w-full h-auto rounded-2xl shadow-2xl relative z-10 animate-fade-in"
              />
            </div>
          </div>
        </div>
      </section>

      {/* --------------------- Latest Updates & Poster Section --------------------- */}
      {(() => {
        // Collect all active updates (only those with titles)
        const updates = [
          { title: settings?.update1Title, description: settings?.update1Description },
          { title: settings?.update2Title, description: settings?.update2Description },
          { title: settings?.update3Title, description: settings?.update3Description },
          { title: settings?.update4Title, description: settings?.update4Description },
          { title: settings?.update5Title, description: settings?.update5Description },
        ].filter(update => update.title);

        const hasAdvertisement = !!settings?.advertisementImageUrl;
        const hasUpdates = updates.length > 0;

        // Don't show section if nothing to display
        if (!hasUpdates && !hasAdvertisement) return null;

        return (
          <section className="py-16 bg-yellow-50">
            <div className="container mx-auto px-4 max-w-7xl">
              {/* Show 2-column layout only when advertisement exists, otherwise show full-width updates in 2 columns */}
              {hasAdvertisement ? (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
                  {/* Latest Updates Block - Left side */}
                  {hasUpdates && (
                    <div className="bg-white rounded-xl shadow-lg p-8 border border-gray-200">
                      <h2 className="text-3xl font-bold text-foreground mb-6">Latest Updates</h2>
                      <div className="space-y-6">
                        {updates.map((update, index) => (
                          <div key={index} className="border-l-4 border-accent pl-4 py-2">
                            <h3 className="text-lg font-semibold text-foreground mb-2">{update.title}</h3>
                            <p className="text-foreground">{update.description}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Advertisement Block - Right side */}
                  <div className="rounded-xl overflow-hidden shadow-lg">
                    <img
                      src={settings.advertisementImageUrl || ''}
                      alt="Tuition Point Advertisement"
                      className="w-full h-auto object-cover"
                    />
                  </div>
                </div>
              ) : (
                /* No advertisement - Show updates in 2 columns grid */
                hasUpdates && (
                  <div>
                    <h2 className="text-3xl font-bold text-foreground mb-8 text-center">Latest Updates</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {updates.map((update, index) => (
                        <div key={index} className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
                          <div className="border-l-4 border-accent pl-4 py-2">
                            <h3 className="text-lg font-semibold text-foreground mb-2">{update.title}</h3>
                            <p className="text-foreground">{update.description}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )
              )}
            </div>
          </section>
        );
      })()}

      {/* --------------------- Features Section (Why Choose) --------------------- */}
     <section className="py-24 bg-accent"> 
    
    {/* Inner container to constrain content width */}
    <div className="container mx-auto px-4 max-w-screen-xl"> 
        
        <div className="text-center max-w-4xl mx-auto mb-16 animate-slide-up">
            <h2 className="text-4xl font-bold text-white mb-4">Why Choose Tuition Point Qatar?</h2>
            <p className="text-lg text-white max-w-2xl mx-auto">Al Wakra's trusted education center for CBSE, IGCSE, and IB students. Our personalized approach helps every student reach their full potential.</p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <FeatureCard 
                key={index}
                icon={feature.icon}
                title={feature.title}
                description={feature.description}
                className={`animate-slide-up animation-delay-${index + 1}`}
              />
            ))}
        </div>
    </div>
</section>
      
      {/* --------------------- Programs Section --------------------- */}
      <section className="py-24 bg-accent">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="text-center max-w-4xl mx-auto mb-16 animate-slide-up">
            <h2 className="text-4xl font-display font-semibold text-white mb-4">Our Programs</h2>
            <p className="text-lg text-white/80 max-w-3xl mx-auto">Comprehensive academic support for students at every level.</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {programs.map((program, index) => (
              <ProgramCard
                key={index}
                tag={program.tag}
                title={program.title}
                description={program.description}
                link={program.link}
                className={`animate-slide-up animation-delay-${index + 1}`}
              />
            ))}
          </div>
        </div>
      </section>
      
      {/* --------------------- Success Stories Section --------------------- */}
      <section className="py-24 bg-accent ">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="text-center max-w-4xl mx-auto mb-16 animate-slide-up">
            <h2 className="text-4xl font-display font-semibold text-white mb-4">Success Stories</h2>
            <p className="text-lg text-white max-w-2xl mx-auto">Our students achieve remarkable results through dedication and our supportive learning environment.</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {successStories.map((story, index) => (
              <SuccessStory 
                key={index}
                name={story.name}
                exam={story.exam}
                percentage={story.percentage}
                testimonial={story.testimonial}
                initial={story.initial}
                className={`animate-slide-up animation-delay-${index + 1}`}
              />
            ))}
          </div>
          
          <div className="text-center mt-16">
            {/* UPDATE: Changed link styling to a prominent secondary button */}
            <Link
              to="/achievements"
              className="bg-white hover:bg-gray-100 text-cyan-600 font-semibold py-3 px-6 rounded-lg shadow-md transition duration-200 animate-slide-up whitespace-nowrap"
            >
              View All Success Stories
            </Link>
          </div>
        </div>
      </section>
      {/* --------------------- Events Section --------------------- */}
      <EventsSection />


      
      {/* --------------------- CTA Section (Ready to Start) --------------------- */}
      <section className="py-24 bg-accent text-white">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-display font-semibold mb-6 animate-slide-up">Ready to Start Your Educational Journey?</h2>
            <p className="text-lg mb-10 text-white text-opacity-90 animate-slide-up">Join Tuition Point Education Center today and take the first step toward academic excellence.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center animate-slide-up">
              {/* UPDATE: Applied explicit Tailwind classes for buttons */}
              <Link 
                to="/enrollment" 
                className="bg-white text-accent hover:bg-gray-100 font-semibold py-3 px-8 rounded-lg shadow-xl transition duration-300  sm:w-auto text-lg"
              >
                Enroll Today
              </Link>
              <Link 
                to="/contact" 
                className="border-2 border-white text-white hover:bg-white hover:text-accent font-semibold py-3 px-8 rounded-lg transition duration-300  sm:w-auto text-lg"
              >
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </section>
      
      {/* --------------------- Testimonials Section (What Parents Say) --------------------- */}
      <section className="py-24 bg-accent">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="text-center max-w-4xl mx-auto mb-16 animate-slide-up">
            <div className="flex items-center justify-center mb-6">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-5 h-5 text-yellow-300 fill-yellow-300" />
              ))}
              <span className="ml-2 text-lg font-medium text-white/80">4.9/5</span>
            </div>
            <h2 className="text-4xl font-display font-semibold text-white mb-4">What Parents Say</h2>
            <p className="text-lg text-white/80 max-w-3xl mx-auto">Based on 42 reviews from our parent community.</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {testimonials.map((testimonial, index) => (
              <TestimonialCard
                key={index}
                rating={testimonial.rating}
                text={testimonial.text}
                name={testimonial.name}
                role={testimonial.role}
                initials={testimonial.initials}
                className={`animate-slide-up animation-delay-${index + 1}`}
              />
            ))}
          </div>
          
          <div className="text-center mt-16">
            <Link to="/reviews" className="text-white text-base font-semibold border-b-2 border-transparent hover:border-white transition duration-200 uppercase tracking-wider animate-slide-up">
              View All Reviews
            </Link>
          </div>
        </div>
      </section>

      {/* --------------------- Quick Links (Explore More) --------------------- */}
      <section className="py-24 bg-accent ">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="text-center max-w-4xl mx-auto mb-16 animate-slide-up">
            <h2 className="text-4xl font-display font-semibold text-white mb-4">Explore More</h2>
            <p className="text-lg text-white max-w-3xl mx-auto">Discover all that Tuition Point Education Center has to offer.</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {quickLinks.map((link, index) => (
                <Link to={link.link} key={index} className="bg-white p-8 rounded-xl shadow-lg flex flex-col items-start transform transition-all duration-300 hover:-translate-y-1 animate-slide-up">
                    <div className="w-14 h-14 bg-accent/10 rounded-xl flex items-center justify-center mb-6">
                      {/* Icon with updated high-contrast color */}
                      {link.icon}
                    </div>
                    <h3 className="text-xl font-display font-semibold mb-3 text-foreground">{link.title}</h3>
                    <p className="text-muted-foreground">{link.description}</p>
                </Link>
            ))}
          </div>
        </div>
      </section>
      
      {/* NOTE: Footer content is omitted for brevity, assuming it's external or separate */}
     
    </div>
  );
};

export default TuitionPointHome;
